// MF-583 — Server health, for the Maestro role only: live requests, errors, latency, memory, busiest endpoints and the
// workers of this instance. Numbers come from the backend's own counters (per-minute buckets), refreshed every 10 s.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import ReactECharts from 'echarts-for-react'
import Module from '../../layout/Module'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowsRotate,
  faBolt,
  faCircleExclamation,
  faClock,
  faMemory,
  faServer
} from '@fortawesome/pro-light-svg-icons'
import {
  getServerErrors,
  getServerSummary,
  getServerWorkers
} from '../../../services/system/server-metrics.service'
import {
  SUMMARY_PALETTE,
  INK,
  FONT,
  baseTextStyle,
  tooltipBase,
  legendBase
} from '../Analytics/Boards/boardTheme'

const RANGES = [
  ['15m', '15 min'],
  ['1h', '1 hour'],
  ['6h', '6 hours'],
  ['24h', '24 hours']
]
const REFRESH_MS = 10000
const BLUE = '#2A69A7'
const ORANGE = '#D77B30'
const PURPLE = '#7b5fbf'

const fmtNum = (n) => (n === null || n === undefined ? '–' : Number(n).toLocaleString('en-AU'))
const fmtMs = (n) => (n === null || n === undefined ? '–' : `${fmtNum(n)} ms`)
const fmtMb = (n) =>
  n === null || n === undefined
    ? '–'
    : `${Number(n).toLocaleString('en-AU', { maximumFractionDigits: 1 })} MB`
const fmtUptime = (s) => {
  if (!Number.isFinite(s)) return '–'
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  return d ? `${d}d ${h}h` : h ? `${h}h ${m}m` : `${m}m`
}
const clock = (iso, withSeconds = false) =>
  iso
    ? new Date(iso).toLocaleTimeString('en-AU', {
        hour: '2-digit',
        minute: '2-digit',
        ...(withSeconds ? { second: '2-digit' } : {})
      })
    : '–'
// green under 200 ms, amber under 1 s, red above: how a teacher would feel the wait
const latencyTone = (ms) =>
  ms === null || ms === undefined
    ? SUMMARY_PALETTE.muted
    : ms < 200
      ? '#2e7d32'
      : ms < 1000
        ? ORANGE
        : '#c0392b'

const Card = ({ title, subtitle, icon, action, children, sx }) => (
  <Paper
    variant='outlined'
    sx={{
      borderRadius: '14px',
      p: 2,
      bgcolor: '#fff',
      borderColor: SUMMARY_PALETTE.grid,
      minWidth: 0,
      ...sx
    }}
  >
    {title ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        {icon ? (
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '10px',
              bgcolor: 'rgba(47,102,144,.1)',
              color: 'primary.main',
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0
            }}
          >
            <FontAwesomeIcon icon={icon} />
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>{title}</Typography>
          {subtitle ? (
            <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>{subtitle}</Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    ) : null}
    {children}
  </Paper>
)

const Stat = ({ label, value, sub, icon, tone }) => (
  <Paper
    variant='outlined'
    sx={{
      borderRadius: '14px',
      p: 2,
      bgcolor: '#fff',
      borderColor: SUMMARY_PALETTE.grid,
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      minWidth: 0
    }}
  >
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: '12px',
        bgcolor: 'rgba(47,102,144,.1)',
        color: tone || 'primary.main',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0
      }}
    >
      <FontAwesomeIcon icon={icon} size='lg' />
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.muted }}>{label}</Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 700, color: SUMMARY_PALETTE.ink, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub ? <Typography sx={{ fontSize: 11.5, color: SUMMARY_PALETTE.muted }}>{sub}</Typography> : null}
    </Box>
  </Paper>
)

const Percentile = ({ label, value, hint }) => (
  <Tooltip arrow title={hint}>
    <Box
      sx={{
        flex: '1 1 110px',
        minWidth: 0,
        p: 1.25,
        borderRadius: '10px',
        bgcolor: '#f6f9fc',
        border: '1px solid #e6edf4'
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: SUMMARY_PALETTE.faint
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: latencyTone(value) }}>
        {fmtMs(value)}
      </Typography>
    </Box>
  </Tooltip>
)

const PRESSURE = {
  ok: { color: '#2A69A7', label: 'OK', chip: 'success' },
  high: { color: '#D77B30', label: 'High memory', chip: 'warning' },
  critical: { color: '#c0392b', label: 'Restart advised', chip: 'error' },
  unknown: { color: '#98a3b3', label: 'No limit data', chip: 'default' }
}

// The page's skeleton has the shape of the page, so nothing jumps when the numbers arrive
const PageSkeleton = () => (
  <>
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', mb: 2 }}>
      {[0, 1, 2, 3].map((i) => (
        <Skeleton key={i} variant='rounded' height={88} sx={{ borderRadius: '14px' }} />
      ))}
    </Box>
    <Skeleton variant='rounded' height={140} sx={{ borderRadius: '14px', mb: 2 }} />
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: 2 }}>
      <Skeleton variant='rounded' height={320} sx={{ borderRadius: '14px' }} />
      <Skeleton variant='rounded' height={320} sx={{ borderRadius: '14px' }} />
    </Box>
  </>
)

const baseChart = {
  textStyle: baseTextStyle,
  animationDuration: 300,
  tooltip: { ...tooltipBase, trigger: 'axis' },
  grid: { top: 36, left: 8, right: 16, bottom: 8, containLabel: true }
}
const timeAxis = (timeline) => ({
  type: 'category',
  data: timeline.map((p) => clock(p.t)),
  axisLine: { lineStyle: { color: INK.axis } },
  axisTick: { show: false },
  axisLabel: { color: INK.secondary, fontSize: 11, fontFamily: FONT, hideOverlap: true }
})
const valueAxis = {
  type: 'value',
  splitLine: { lineStyle: { type: 'dashed', color: INK.grid } },
  axisLabel: { color: INK.muted, fontSize: 11 }
}

const STATUS_COLORS = { '2xx': '#2A69A7', '3xx': '#87A9D7', '4xx': '#F3A452', '5xx': '#B45B23' }
const donut = (items, nameKey, colors) => ({
  textStyle: baseTextStyle,
  tooltip: { ...tooltipBase, trigger: 'item', formatter: '{b}: {c} ({d}%)' },
  legend: { ...legendBase, bottom: 0 },
  color: colors,
  series: [
    {
      type: 'pie',
      radius: ['48%', '72%'],
      center: ['50%', '44%'],
      avoidLabelOverlap: true,
      label: { show: false },
      data: items.map((m) => ({ name: m[nameKey], value: m.count }))
    }
  ]
})

const Table = ({ columns, rows, empty, rowKey }) =>
  rows.length === 0 ? (
    <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, py: 2, textAlign: 'center' }}>
      {empty}
    </Typography>
  ) : (
    <Box sx={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
      <Box
        component='table'
        sx={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5, fontFamily: FONT }}
      >
        <thead>
          <tr>
            {columns.map((c) => (
              <Box
                key={c.key}
                component='th'
                sx={{
                  position: 'sticky',
                  top: 0,
                  bgcolor: '#fff',
                  textAlign: c.align || 'left',
                  px: 1.5,
                  py: 0.75,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '.03em',
                  textTransform: 'uppercase',
                  color: SUMMARY_PALETTE.faint,
                  borderBottom: `1px solid ${SUMMARY_PALETTE.grid}`,
                  whiteSpace: 'nowrap'
                }}
              >
                {c.label}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <Box key={rowKey(r, i)} component='tr' sx={{ bgcolor: i % 2 ? '#fafbfd' : '#fff' }}>
              {columns.map((c) => (
                <Box
                  key={c.key}
                  component='td'
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    textAlign: c.align || 'left',
                    color: c.muted ? SUMMARY_PALETTE.muted : SUMMARY_PALETTE.ink,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: c.wrap ? 'normal' : 'nowrap',
                    overflowWrap: 'anywhere',
                    ...(c.mono ? { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 } : {})
                  }}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </Box>
              ))}
            </Box>
          ))}
        </tbody>
      </Box>
    </Box>
  )

const ServerHealth = () => {
  const isMaestro = useSelector((state) =>
    (state?.session?.user?.roles || []).some((r) => (r?.id || r?.name) === 'Maestro')
  )
  const [range, setRange] = useState('1h')
  const [includeHealth, setIncludeHealth] = useState(false)
  const [server, setServer] = useState('all') // 'all' = every server that reports to this database
  const [auto, setAuto] = useState(true)
  const [summary, setSummary] = useState(null)
  const [workers, setWorkers] = useState(null)
  const [errors, setErrors] = useState([])
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [updatedAt, setUpdatedAt] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [s, w, e] = await Promise.all([
        getServerSummary({ range, includeHealth, server: server === 'all' ? null : server }),
        getServerWorkers(),
        getServerErrors(30, server === 'all' ? null : server)
      ])
      setSummary(s)
      setWorkers(w)
      setErrors(e || [])
      setError(null)
      setUpdatedAt(new Date())
    } catch (err) {
      setError(err?.message || 'The server metrics could not be loaded')
    } finally {
      setLoading(false)
    }
  }, [range, includeHealth, server])

  useEffect(() => {
    load()
  }, [load])
  useEffect(() => {
    if (!auto) return undefined
    const id = setInterval(load, REFRESH_MS)
    return () => clearInterval(id)
  }, [auto, load])

  const t = summary?.totals
  const timeline = useMemo(() => summary?.timeline || [], [summary])

  const activityOption = useMemo(
    () => ({
      ...baseChart,
      legend: { ...legendBase, top: 0 },
      xAxis: timeAxis(timeline),
      yAxis: valueAxis,
      series: [
        {
          name: 'Requests',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: timeline.map((p) => p.requests),
          lineStyle: { color: BLUE, width: 2 },
          itemStyle: { color: BLUE },
          areaStyle: { color: BLUE, opacity: 0.08 }
        },
        {
          name: 'Errors',
          type: 'line',
          smooth: true,
          showSymbol: false,
          data: timeline.map((p) => p.errors),
          lineStyle: { color: ORANGE, width: 2 },
          itemStyle: { color: ORANGE }
        }
      ]
    }),
    [timeline]
  )
  const memoryOption = useMemo(
    () => ({
      ...baseChart,
      legend: { ...legendBase, top: 0 },
      tooltip: {
        ...tooltipBase,
        trigger: 'axis',
        valueFormatter: (v) => (v === null || v === undefined ? '–' : `${v} MB`)
      },
      xAxis: timeAxis(timeline),
      yAxis: valueAxis,
      series: [
        {
          name: 'Heap used',
          type: 'line',
          smooth: true,
          showSymbol: false,
          connectNulls: true,
          data: timeline.map((p) => p.heapUsedMb),
          lineStyle: { color: PURPLE, width: 2 },
          itemStyle: { color: PURPLE },
          areaStyle: { color: PURPLE, opacity: 0.1 }
        },
        {
          name: 'RSS (all memory)',
          type: 'line',
          smooth: true,
          showSymbol: false,
          connectNulls: true,
          data: timeline.map((p) => p.rssMb),
          lineStyle: { color: BLUE, width: 2 },
          itemStyle: { color: BLUE }
        }
      ]
    }),
    [timeline]
  )
  const statusOption = useMemo(
    () =>
      donut(
        summary?.statuses || [],
        'klass',
        (summary?.statuses || []).map((x) => STATUS_COLORS[x.klass] || '#98a3b3')
      ),
    [summary]
  )
  const methodOption = useMemo(
    () => donut(summary?.methods || [], 'method', ['#2A69A7', '#F3A452', '#87A9D7', '#B45B23', '#162F62']),
    [summary]
  )

  const allWorkers = workers?.workers || []
  const shownWorkers = server === 'all' ? allWorkers : allWorkers.filter((w) => w.instance === server)
  const serverNames = workers?.servers?.map((x) => x.instance) || []
  const multi = serverNames.length > 1
  const shortName = (w) => (multi ? `${w.instance.replace(/^ip-/, '')} · ${w.pid}` : `Worker ${w.pid}`)
  const pressureOption = useMemo(
    () => ({
      ...baseChart,
      grid: { top: 12, left: 8, right: 64, bottom: 8, containLabel: true },
      tooltip: {
        ...tooltipBase,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (ps) => {
          const w = shownWorkers[ps[0].dataIndex]
          return `<b>${shortName(w)}</b><br/>${fmtMb(w.heapUsedMb)} of ${fmtMb(w.heapLimitMb)} (${w.heapPct ?? '–'}%)<br/>${PRESSURE[w.memory]?.label || ''}${w.state === 'stale' ? ' · not reporting' : ''}`
        }
      },
      xAxis: {
        ...valueAxis,
        min: 0,
        max: 100,
        axisLabel: { color: INK.muted, fontSize: 11, formatter: '{value}%' }
      },
      yAxis: {
        type: 'category',
        inverse: true,
        data: shownWorkers.map(shortName),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: INK.axis } },
        axisLabel: { color: INK.secondary, fontSize: 11 }
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 18,
          data: shownWorkers.map((w) => ({
            value: w.heapPct ?? 0,
            itemStyle: {
              color: w.state === 'stale' ? '#c5ccd6' : PRESSURE[w.memory]?.color,
              borderRadius: [0, 4, 4, 0]
            }
          })),
          label: {
            show: true,
            position: 'right',
            fontSize: 11,
            color: INK.secondary,
            formatter: (p) => `${p.value}%`
          },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { type: 'dashed', color: '#98a3b3' },
            label: { fontSize: 10, color: INK.muted },
            data: [
              { xAxis: 70, label: { formatter: '70%', position: 'end' } },
              { xAxis: 85, label: { formatter: '85%', position: 'end' } }
            ]
          }
        }
      ]
    }),
    [shownWorkers, multi] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const topEndpoints = (summary?.endpoints || []).slice(0, 12)
  const endpointsOption = useMemo(
    () => ({
      ...baseChart,
      grid: { top: 8, left: 8, right: 56, bottom: 8, containLabel: true },
      tooltip: { ...tooltipBase, trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: valueAxis,
      yAxis: {
        type: 'category',
        inverse: true,
        data: topEndpoints.map((e) => e.endpoint.replace('/API/v2', '')),
        axisLabel: { color: INK.secondary, fontSize: 11, width: 220, overflow: 'truncate' },
        axisTick: { show: false },
        axisLine: { lineStyle: { color: INK.axis } }
      },
      series: [
        {
          type: 'bar',
          barMaxWidth: 20,
          data: topEndpoints.map((e) => ({
            value: e.count,
            itemStyle: { color: e.health ? '#98a3b3' : BLUE, borderRadius: [0, 4, 4, 0] }
          })),
          label: { show: true, position: 'right', fontSize: 11, color: INK.secondary }
        }
      ]
    }),
    [topEndpoints]
  )

  if (!isMaestro) return <Navigate to='/dashboard' replace />

  const hasTraffic = (t?.requests || 0) > 0
  return (
    <Module>
      <Module.Header title='Server health' />
      <Module.Body>
        <Module.BodyFullContainer className='overflow-y-auto'>
          <Box sx={{ p: { xs: 1, md: 2 }, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
              <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
                <Typography sx={{ fontSize: 22, fontWeight: 700, color: SUMMARY_PALETTE.ink }}>
                  Server health
                </Typography>
                <Typography sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted }}>
                  Live traffic, errors, latency and memory of this server
                  {summary?.instance ? ` (${summary.instance})` : ''}. Only the Maestro role sees this.
                </Typography>
              </Box>
              {multi ? (
                <ToggleButtonGroup
                  size='small'
                  exclusive
                  value={server}
                  onChange={(_, v) => v && setServer(v)}
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
                >
                  <ToggleButton value='all'>All servers ({serverNames.length})</ToggleButton>
                  {serverNames.map((n) => (
                    <ToggleButton key={n} value={n}>
                      {n.replace(/^ip-/, '')}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : null}
              <ToggleButtonGroup
                size='small'
                exclusive
                value={range}
                onChange={(_, v) => v && setRange(v)}
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
              >
                {RANGES.map(([v, label]) => (
                  <ToggleButton key={v} value={v}>
                    {label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Chip
                size='small'
                clickable
                color={auto ? 'success' : 'default'}
                variant={auto ? 'filled' : 'outlined'}
                label={auto ? 'Auto-refresh 10s' : 'Auto-refresh off'}
                onClick={() => setAuto((a) => !a)}
              />
              <Button
                size='small'
                variant='outlined'
                onClick={load}
                disabled={loading}
                startIcon={<FontAwesomeIcon icon={faArrowsRotate} spin={loading} />}
              >
                Refresh
              </Button>
            </Box>
            {updatedAt ? (
              <Typography sx={{ fontSize: 12, color: SUMMARY_PALETTE.faint, mb: 1.5 }}>
                Last updated {clock(updatedAt, true)}
              </Typography>
            ) : null}
            {error ? (
              <Alert severity='error' sx={{ mb: 2, borderRadius: '12px' }}>
                {error}
              </Alert>
            ) : null}
            {summary && summary.enabled === false ? (
              <Alert severity='warning' sx={{ mb: 2, borderRadius: '12px' }}>
                Server metrics are switched off on this server (SERVER_METRICS_ENABLED=false).
              </Alert>
            ) : null}

            {!summary ? (
              <PageSkeleton />
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                    mb: 2
                  }}
                >
                  <Stat
                    icon={faBolt}
                    label='Requests'
                    value={fmtNum(t.requests)}
                    sub={`${fmtNum(t.healthChecks)} health checks, not counted`}
                  />
                  <Stat
                    icon={faCircleExclamation}
                    tone={t.errors ? '#c0392b' : undefined}
                    label='Server errors (5xx)'
                    value={fmtNum(t.errors)}
                    sub={`${t.errorRate}% of requests`}
                  />
                  <Stat
                    icon={faMemory}
                    label='Memory (heap, average)'
                    value={fmtMb(summary.memory.heapUsedMb.avg)}
                    sub={`peak ${fmtMb(summary.memory.heapUsedMb.max)} · RSS ${fmtMb(summary.memory.rssMb.avg)}`}
                  />
                  <Stat
                    icon={faServer}
                    label={multi ? 'Servers · workers' : 'Workers'}
                    value={
                      workers
                        ? multi
                          ? `${workers.totals.servers} · ${workers.totals.live}/${workers.totals.workers}`
                          : `${workers.totals.live} / ${workers.totals.workers}`
                        : '–'
                    }
                    sub={workers ? `live · ${fmtMb(workers.totals.rssMb)} RSS in total` : undefined}
                  />
                </Box>

                {!hasTraffic ? (
                  <Alert severity='info' sx={{ mb: 2, borderRadius: '12px' }}>
                    No requests in this range yet. Counters are saved every 10 seconds, so the first numbers
                    appear shortly after the server starts.
                  </Alert>
                ) : null}

                {workers && workers.totals.needRestart > 0 ? (
                  <Alert severity='error' sx={{ mb: 2, borderRadius: '12px' }}>
                    {workers.totals.needRestart} worker{workers.totals.needRestart === 1 ? ' is' : 's are'}{' '}
                    above 85% of the memory limit: restart {workers.totals.needRestart === 1 ? 'it' : 'them'}{' '}
                    before the server stops answering.
                  </Alert>
                ) : workers && workers.totals.highMemory > 0 ? (
                  <Alert severity='warning' sx={{ mb: 2, borderRadius: '12px' }}>
                    {workers.totals.highMemory} worker{workers.totals.highMemory === 1 ? '' : 's'} above 70%
                    of the memory limit. Keep an eye on {workers.totals.highMemory === 1 ? 'it' : 'them'}.
                  </Alert>
                ) : null}

                <Card
                  title='Servers, live'
                  subtitle={
                    workers
                      ? `${workers.totals.servers} server${workers.totals.servers === 1 ? '' : 's'} · ${workers.totals.workers} worker${workers.totals.workers === 1 ? '' : 's'} · memory used out of each worker's limit (dashed: 70% high, 85% restart)`
                      : undefined
                  }
                  icon={faServer}
                  sx={{ mb: 2 }}
                >
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', lg: '4fr 8fr' },
                      alignItems: 'start'
                    }}
                  >
                    {shownWorkers.length ? (
                      <ReactECharts
                        option={pressureOption}
                        notMerge
                        style={{ height: Math.max(140, shownWorkers.length * 30 + 24) }}
                      />
                    ) : (
                      <Typography
                        sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, py: 3, textAlign: 'center' }}
                      >
                        No worker has reported yet.
                      </Typography>
                    )}
                    <Table
                      rowKey={(r) => `${r.instance}-${r.pid}`}
                      rows={shownWorkers}
                      empty='No worker has reported yet.'
                      columns={[
                        ...(multi
                          ? [
                              {
                                key: 'instance',
                                label: 'Server',
                                muted: true,
                                render: (r) => r.instance.replace(/^ip-/, '')
                              }
                            ]
                          : []),
                        { key: 'pid', label: 'PID' },
                        { key: 'version', label: 'Version', muted: true },
                        { key: 'uptimeSec', label: 'Uptime', render: (r) => fmtUptime(r.uptimeSec) },
                        {
                          key: 'heapUsedMb',
                          label: 'Heap',
                          align: 'right',
                          render: (r) =>
                            `${fmtMb(r.heapUsedMb)}${r.heapPct !== null ? ` · ${r.heapPct}%` : ''}`
                        },
                        { key: 'rssMb', label: 'RSS', align: 'right', render: (r) => fmtMb(r.rssMb) },
                        {
                          key: 'lastSeen',
                          label: 'Last seen',
                          muted: true,
                          render: (r) => clock(r.lastSeen, true)
                        },
                        {
                          key: 'state',
                          label: 'Status',
                          render: (r) =>
                            r.state === 'stale' ? (
                              <Chip
                                size='small'
                                variant='outlined'
                                label='Not reporting'
                                sx={{ height: 20 }}
                              />
                            ) : (
                              <Chip
                                size='small'
                                color={PRESSURE[r.memory]?.chip}
                                variant='outlined'
                                label={PRESSURE[r.memory]?.label}
                                sx={{ height: 20 }}
                              />
                            )
                        }
                      ]}
                    />
                  </Box>
                </Card>

                <Card
                  title='Response time'
                  subtitle='How long the server takes to answer (health checks excluded)'
                  icon={faClock}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
                    <Percentile label='Average' value={t.avgMs} hint='Mean time of every request' />
                    <Percentile label='Fastest' value={t.minMs} hint='The quickest request' />
                    <Percentile
                      label='Median (p50)'
                      value={t.p50}
                      hint='Half of the requests were faster than this'
                    />
                    <Percentile
                      label='p95'
                      value={t.p95}
                      hint='95 of every 100 requests were faster than this'
                    />
                    <Percentile
                      label='p99'
                      value={t.p99}
                      hint='99 of every 100 requests were faster than this'
                    />
                    <Percentile label='Slowest' value={t.maxMs} hint='The slowest request in the range' />
                  </Box>
                </Card>

                <Box
                  sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: 2 }}
                >
                  <Card title='Activity' subtitle='Requests and server errors over time' icon={faBolt}>
                    <ReactECharts option={activityOption} notMerge style={{ height: 260 }} />
                  </Card>
                  <Card title='Memory' subtitle='Average across the workers' icon={faMemory}>
                    <ReactECharts option={memoryOption} notMerge style={{ height: 260 }} />
                  </Card>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 2,
                    gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr 1fr' },
                    mb: 2
                  }}
                >
                  <Card
                    title='Busiest endpoints'
                    subtitle='Most requested routes'
                    action={
                      <FormControlLabel
                        sx={{ mr: 0 }}
                        control={
                          <Checkbox
                            size='small'
                            checked={includeHealth}
                            onChange={(e) => setIncludeHealth(e.target.checked)}
                          />
                        }
                        label={<Typography sx={{ fontSize: 12 }}>Include health checks</Typography>}
                      />
                    }
                  >
                    {topEndpoints.length ? (
                      <ReactECharts
                        option={endpointsOption}
                        notMerge
                        style={{ height: Math.max(180, topEndpoints.length * 26 + 16) }}
                      />
                    ) : (
                      <Typography
                        sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, py: 3, textAlign: 'center' }}
                      >
                        No requests yet.
                      </Typography>
                    )}
                  </Card>
                  <Card title='Methods' subtitle='GET, POST…'>
                    {summary.methods.length ? (
                      <ReactECharts option={methodOption} notMerge style={{ height: 240 }} />
                    ) : (
                      <Typography
                        sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, py: 3, textAlign: 'center' }}
                      >
                        No requests yet.
                      </Typography>
                    )}
                  </Card>
                  <Card title='Status codes' subtitle='2xx fine · 4xx client · 5xx server'>
                    {summary.statuses.length ? (
                      <ReactECharts option={statusOption} notMerge style={{ height: 240 }} />
                    ) : (
                      <Typography
                        sx={{ fontSize: 13, color: SUMMARY_PALETTE.muted, py: 3, textAlign: 'center' }}
                      >
                        No requests yet.
                      </Typography>
                    )}
                  </Card>
                </Box>

                <Card
                  title='Endpoint details'
                  subtitle={`${summary.endpoints.length} routes in this range`}
                  sx={{ mb: 2 }}
                >
                  <Table
                    rowKey={(r) => r.endpoint}
                    rows={summary.endpoints}
                    empty='No requests yet.'
                    columns={[
                      {
                        key: 'endpoint',
                        label: 'Endpoint',
                        mono: true,
                        wrap: true,
                        render: (r) => (
                          <>
                            {r.endpoint}
                            {r.health ? (
                              <Chip size='small' label='Health' sx={{ ml: 1, height: 18, fontSize: 10.5 }} />
                            ) : null}
                          </>
                        )
                      },
                      { key: 'count', label: 'Requests', align: 'right', render: (r) => fmtNum(r.count) },
                      {
                        key: 'errors',
                        label: 'Errors',
                        align: 'right',
                        render: (r) => (
                          <span style={{ color: r.errors ? '#c0392b' : undefined }}>{fmtNum(r.errors)}</span>
                        )
                      },
                      {
                        key: 'avgMs',
                        label: 'Avg time',
                        align: 'right',
                        render: (r) => <span style={{ color: latencyTone(r.avgMs) }}>{fmtMs(r.avgMs)}</span>
                      }
                    ]}
                  />
                </Card>

                <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr' }}>
                  <Card
                    title='Recent errors'
                    subtitle='Latest answers with a server error (5xx)'
                    icon={faCircleExclamation}
                  >
                    <Table
                      rowKey={(r, i) => `${r.at}-${i}`}
                      rows={errors}
                      empty='No server errors lately.'
                      columns={[
                        { key: 'at', label: 'When', muted: true, render: (r) => clock(r.at, true) },
                        {
                          key: 'status',
                          label: 'Code',
                          render: (r) => (
                            <Chip
                              size='small'
                              color='error'
                              variant='outlined'
                              label={r.status}
                              sx={{ height: 20 }}
                            />
                          )
                        },
                        { key: 'endpoint', label: 'Endpoint', mono: true, wrap: true },
                        {
                          key: 'message',
                          label: 'Reason',
                          wrap: true,
                          muted: true,
                          render: (r) => r.message || '–'
                        }
                      ]}
                    />
                  </Card>
                </Box>
              </>
            )}
          </Box>
        </Module.BodyFullContainer>
      </Module.Body>
    </Module>
  )
}

export default ServerHealth
